(function(){
  const globalNamespace = window.FCT = window.FCT || {};
  const swapState = globalNamespace.mainContentSwap = globalNamespace.mainContentSwap || {};

  const previousInstance = window.swapMainContent;
  // Tear down an older instance so we do not leave duplicate listeners behind when sections re-render.
  if (previousInstance?.__listener) {
    document.removeEventListener('click', previousInstance.__listener);
  }
  if (previousInstance?.__popstateListener) {
    window.removeEventListener('popstate', previousInstance.__popstateListener);
  }
  if (typeof previousInstance?.abortActiveSwap === 'function') {
    try {
      previousInstance.abortActiveSwap();
    } catch (error) {
      console.error(error);
    }
  }

  // Shared caches are stored on the namespace so page fragments survive between hot reloads or section swaps.
  const CACHE = swapState.cache || (swapState.cache = new Map());
  const INFLIGHT = swapState.inflight || (swapState.inflight = new Map());
  const PARSER = new DOMParser();
  const CACHE_TTL = 300000;
  const MAX_CACHE_ENTRIES = 3;
  const WARMED_HANDLES = swapState.warmedHandles || (swapState.warmedHandles = (previousInstance?.prefetchedHandles instanceof Set ? previousInstance.prefetchedHandles : new Set())); // track handles we already prefetched within this session
  let activeSwapController = null;

  function getHandle(input) {
    if (!input) return null;
    if (typeof input === 'string') return input;
    if (input instanceof Element) {
      return input.getAttribute('data-product-handle') || input.dataset?.productHandle || null;
    }
    return input.handle || input.product?.handle || null;
  }

  function getShopRoot() {
    const parts = String(window.location.pathname || '').split('/').filter(Boolean);
    const productIndex = parts.indexOf('products');

    if (productIndex >= 0) {
      const prefix = parts.slice(0, productIndex).join('/');
      return prefix ? `/${prefix}/` : '/';
    }

    const root = window.Shopify?.routes?.root;
    if (typeof root === 'string' && root.trim() !== '') return root.endsWith('/') ? root : `${root}/`;
    return '/';
  }

  function buildProductURL(handle) {
    const pathname = `${getShopRoot()}products/${encodeURIComponent(handle)}`;
    const search = new URLSearchParams(window.location.search || '');
    const persistent = new URLSearchParams();
    const additionalParams = String(window.theme?.settings?.pdp_bundle_builder_url_params || '')
      .split(',')
      .map((param) => param.trim())
      .filter((param, index, list) => param && param !== 'pdp_bundle' && list.indexOf(param) === index);

    if (search.get('pdp_bundle') === '1') {
      persistent.set('pdp_bundle', '1');
    }

    if (!persistent.has('pdp_bundle') && additionalParams.some((param) => search.has(param))) {
      persistent.set('pdp_bundle', '1');
    }

    const query = persistent.toString();
    return query ? `${pathname}?${query}` : pathname;
  }

  function getHandleFromPathname(pathname = window.location.pathname) {
    const parts = String(pathname || '').split('/').filter(Boolean);
    const productIndex = parts.indexOf('products');
    if (productIndex < 0 || !parts[productIndex + 1]) return null;

    try {
      return decodeURIComponent(parts[productIndex + 1]);
    } catch (error) {
      return parts[productIndex + 1];
    }
  }

  function buildHistoryState(handle, url) {
    return {
      ...(window.history.state && typeof window.history.state === 'object' ? window.history.state : {}),
      fctPdpSwap: true,
      handle,
      url
    };
  }

  function syncHistory(handle, url, mode = 'auto') {
    if (!url || mode === 'skip') return;

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentUrl === url) return;

    const state = buildHistoryState(handle, url);

    if (mode === 'replace') {
      window.history.replaceState(state, '', url);
      return;
    }

    window.history.pushState(state, '', url);
  }

  function reviveScripts(container) {
    const scripts = Array.from(container.querySelectorAll('script'));
    for (const old of scripts) {
      const s = document.createElement('script');
      // keep attrs
      for (const { name, value } of Array.from(old.attributes)) s.setAttribute(name, value);
      // copy inline code
      if (old.textContent) s.textContent = old.textContent;
      // replace
      old.replaceWith(s);
    }
  }

  function readCache(key) {
    const cached = CACHE.get(key);
    if (!cached) return null;
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      CACHE.delete(key);
      return null;
    }
    return cached.value;
  }

  function writeCache(key, value) {
    // Keep a tiny LRU cache to avoid retaining large HTML blobs for many products.
    if (CACHE.has(key)) {
      CACHE.delete(key);
    }
    CACHE.set(key, { value, timestamp: Date.now() });

    while (CACHE.size > MAX_CACHE_ENTRIES) {
      const oldestKey = CACHE.keys().next().value;
      if (!oldestKey) break;
      CACHE.delete(oldestKey);
    }
  }

  function resetCache() {
    CACHE.clear();
    INFLIGHT.clear();
  }

  function getSectionIdFromElement(element) {
    if (!element) return null;
    if (element.dataset && element.dataset.sectionId) return element.dataset.sectionId;
    if (element.id && element.id.startsWith('shopify-section-')) {
      return element.id.slice('shopify-section-'.length);
    }
    return element.id || null;
  }

  function getSectionTargets(root = document) {
    const selectors = [
      { key: 'breadcrumbs', className: 'section-breadcrumbs' },
      { key: 'product', className: 'product-section' }
    ];
    const targets = [];
    selectors.forEach(({ key, className }) => {
      const element = root.querySelector(`.shopify-section.${className}`);
      if (!element) return;
      const id = getSectionIdFromElement(element);
      if (!id) return;
      targets.push({ key, id, element });
    });
    return targets;
  }

  // Fetch section HTML for the incoming handle; collapses concurrent requests to the same product/section tuple.
  async function fetchSectionsData(handle, sectionIds, { cache = true, signal } = {}) {
    if (!Array.isArray(sectionIds) || !sectionIds.length) return {};
    const cacheKey = `${handle}|${sectionIds.join(',')}`;
    if (cache) {
      const cached = readCache(cacheKey);
      if (cached) return cached;
      const inflight = INFLIGHT.get(cacheKey);
      if (inflight) return inflight;
    }

    const query = new URLSearchParams();
    query.set('sections', sectionIds.join(','));
    const requestUrl = new URL(buildProductURL(handle), window.location.origin);
    requestUrl.searchParams.set('sections', sectionIds.join(','));

    const request = fetch(requestUrl.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal
    }).then((res) => {
      if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
      return res.json();
    }).then((data) => {
      if (cache) writeCache(cacheKey, data);
      return data;
    }).finally(() => {
      INFLIGHT.delete(cacheKey);
    });

    if (cache) INFLIGHT.set(cacheKey, request);

    return request;
  }

  function dispatchSwapEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(`pdp:swap:${name}`, { detail }));
  }

  function escapeAttributeValue(value) {
    const stringValue = value == null ? '' : String(value);
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(stringValue);
    }
    return stringValue.replace(/["\\]/g, '\\$&');
  }

  function readFieldsetValue(fieldset) {
    if (!fieldset) return '';
    const select = fieldset.querySelector('select');
    if (select) return select.value;
    const checked = fieldset.querySelector('input:checked');
    return checked ? checked.value : '';
  }

  // Snapshot currently selected variant options so we can replay them after a swap.
  function captureVariantState(container) {
    if (!container) return null;
    const selector = container.querySelector('variant-selects');
    if (!selector) return null;
    const fieldsets = Array.from(selector.querySelectorAll('fieldset'));
    if (!fieldsets.length) return null;
    const options = fieldsets.map((fieldset, index) => {
      const value = readFieldsetValue(fieldset);
      return {
        handle: fieldset.getAttribute('data-handle') || null,
        index,
        value,
        hasValue: value !== undefined && value !== null && String(value).trim() !== ''
      };
    });
    const hasSelected = options.some((option) => option.hasValue);
    return { options, hasSelected, timestamp: Date.now() };
  }

  function setFieldsetValue(fieldset, value) {
    if (!fieldset) return false;
    const targetValue = value == null ? '' : String(value);
    if (!targetValue) return false;

    const select = fieldset.querySelector('select');
    if (select) {
      const options = Array.from(select.options);
      let match = options.find((opt) => opt.value === targetValue);
      if (!match) {
        match = options.find((opt) => opt.value.toLowerCase() === targetValue.toLowerCase());
      }
      if (!match || select.value === match.value) return false;
      select.value = match.value;
      return true;
    }

    const escaped = escapeAttributeValue(targetValue);
    let input = fieldset.querySelector(`input[value="${escaped}"]`);
    if (!input) {
      input = Array.from(fieldset.querySelectorAll('input[value]')).find((el) => el.value.toLowerCase() === targetValue.toLowerCase());
    }
    if (!input || input.checked) return false;
    input.checked = true;
    return true;
  }

  // Apply a recorded option selection back onto a variant-selects instance.
  function applyOptionsToSelector(selector, optionSnapshot) {
    if (!selector || !Array.isArray(optionSnapshot)) {
      return { applied: false, matched: [] };
    }
    const fieldsets = Array.from(selector.querySelectorAll('fieldset'));
    if (!fieldsets.length) {
      return { applied: false, matched: [] };
    }

    const mapped = new Map();
    fieldsets.forEach((fieldset, index) => {
      const handle = fieldset.getAttribute('data-handle') || null;
      if (handle) mapped.set(`handle:${handle}`, fieldset);
      mapped.set(`index:${index}`, fieldset);
    });

    let changed = false;
    const matched = [];
    optionSnapshot.forEach((snapshot) => {
      if (!snapshot || !snapshot.hasValue) return;
      const key = snapshot.handle ? `handle:${snapshot.handle}` : `index:${snapshot.index}`;
      const fallbackKey = `index:${snapshot.index}`;
      const fieldset = mapped.get(key) || mapped.get(fallbackKey);
      if (!fieldset) return;
      if (setFieldsetValue(fieldset, snapshot.value)) {
        changed = true;
        matched.push({ handle: snapshot.handle || null, index: snapshot.index, value: snapshot.value });
      }
    });

    return { applied: changed, matched };
  }

  function readCurrentVariantId(container, sectionId) {
    if (sectionId) {
      const input = container.querySelector(`#product-form-${sectionId} input[name="id"]`);
      if (input && input.value) return input.value;
    }
    const fallback = container.querySelector('form[action*="/cart/add"] input[name="id"]');
    return fallback && fallback.value ? fallback.value : null;
  }

  function nextFrame() {
    return new Promise((resolve) => {
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => resolve());
      } else {
        setTimeout(resolve, 16);
      }
    });
  }

  // Wait for the variant component to boot, then reapply the shopper's previous selection after the DOM swap.
  async function restoreVariantState(container, snapshot) {
    const result = { applied: false, matched: [], variantId: null };
    if (!snapshot || !snapshot.hasSelected) return result;

    if (typeof customElements !== 'undefined' && typeof customElements.whenDefined === 'function') {
      try {
        await customElements.whenDefined('variant-selects');
      } catch (error) {
        /* noop */
      }
    }

    await nextFrame();

    const selector = container.querySelector('variant-selects');
    if (!selector) return result;

    const { applied, matched } = applyOptionsToSelector(selector, snapshot.options);
    const shouldTriggerChange = snapshot.hasSelected;
    if (shouldTriggerChange) {
      if (typeof selector.onVariantChange === 'function') {
        const originalRenderProductInfo = selector.renderProductInfo;
        selector.renderProductInfo = function lightweightRenderProductInfo() {
          const strings = window.theme?.variantStrings || {};
          const soldOutText = typeof strings.soldOut === 'string' ? strings.soldOut : 'Sold Out';
          if (!this.currentVariant) {
            this.toggleAddButton(true, soldOutText, true);
            if (typeof this.setUnavailable === 'function') {
              this.setUnavailable();
            }
            return;
          }
          this.toggleAddButton(!this.currentVariant.available, soldOutText, true);
        };
        try {
          selector.onVariantChange();
        } catch (error) {
          console.error(error);
          selector.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          selector.renderProductInfo = originalRenderProductInfo;
        }
      } else {
        selector.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    result.applied = applied;
    result.matched = matched;
    result.triggered = shouldTriggerChange;
    result.variantId = readCurrentVariantId(container, selector.getAttribute('data-section') || '');

    return result;
  }

  async function swapMainContent(product, opts = {}) {
    const handle = getHandle(product);
    if (!handle) throw new Error('handle required');
    const target = document.getElementById('main-content');
    if (!target) throw new Error('#main-content not found on current page');

    const sectionTargets = getSectionTargets();
    if (!sectionTargets.length) throw new Error('No section targets found for swap');
    const sectionIds = sectionTargets.map((section) => section.id);

    const previousVariant = captureVariantState(target); // remember active variant before we swap the DOM

    if (activeSwapController) {
      activeSwapController.abort();
    }

    const controller = new AbortController();
    activeSwapController = controller;

    const url = buildProductURL(handle);
    const detail = {
      handle,
      url,
      trigger: opts.trigger || null,
      previousVariant,
      sections: sectionTargets.map(({ id, key }) => ({ id, key }))
    };
    dispatchSwapEvent('start', detail);

    let sectionsHtml;
    try {
      sectionsHtml = await fetchSectionsData(handle, sectionIds, { cache: opts.cache !== false, signal: controller.signal });
    } catch (error) {
      if (error?.name === 'AbortError') {
        if (activeSwapController === controller) {
          activeSwapController = null;
        }
        return;
      }
      dispatchSwapEvent('error', { ...detail, error });
      throw error;
    }

    const keepScroll = opts.scroll === 'keep';
    const skipScroll = opts.scroll === 'none';
    const scrollY = window.scrollY;

    sectionTargets.forEach(({ id, element }) => {
      const htmlFragment = sectionsHtml?.[id];
      if (typeof htmlFragment === 'string') {
        element.innerHTML = htmlFragment;
        reviveScripts(element);
      } else {
        console.warn(`Missing section content for ${id}`);
      }
    });

    target.dataset.currentHandle = handle;

    syncHistory(handle, url, opts.historyMode || 'auto');

    const originalTitle = document.title;

    const productTarget = sectionTargets.find((section) => section.key === 'product');
    if (productTarget) {
      const htmlFragment = sectionsHtml?.[productTarget.id];
      if (typeof htmlFragment === 'string') {
        const doc = PARSER.parseFromString(htmlFragment, 'text/html');
        const heading = doc.querySelector('.product-title');
        if (heading) {
          const currentTitle = originalTitle;
          const delimiter = ' – ';
          const suffix = currentTitle.includes(delimiter)
            ? currentTitle.split(delimiter).slice(1).join(delimiter).trim()
            : '';
          const newTitle = heading.textContent.trim();
          if (newTitle) {
            document.title = suffix ? `${newTitle}${delimiter} ${suffix}` : newTitle;
          }
        }
      }
    }

    if (skipScroll) {
      // Let the browser restore scroll position on history navigation.
    } else if (keepScroll) {
      window.scrollTo({ top: scrollY, behavior: 'instant' in window ? 'instant' : 'auto' });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    let restoreResult = { applied: false, matched: [], variantId: null };
      try {
        restoreResult = await restoreVariantState(target, previousVariant);
      } catch (error) {
        console.error(error);
      }
      // Ensure price blocks are visible after swap (pack changes may skip full render).
      target.querySelectorAll('[id^="price-"]').forEach((priceEl) => {
        priceEl.classList.remove('visibility-hidden');
      });
      detail.restore = restoreResult;

    const payload = {
      handle,
      url,
      container: target,
      previousVariant,
      restore: restoreResult,
      sections: sectionTargets,
      html: sectionsHtml
    };

    if (typeof opts.afterRender === 'function') {
      try { opts.afterRender(payload); } catch (error) { console.error(error); }
    }

    dispatchSwapEvent('success', { ...detail, container: target, restore: restoreResult, sectionsHtml });
    if (activeSwapController === controller) {
      activeSwapController = null;
    }

    return payload;
  }

  function handlePopState() {
    const target = document.getElementById('main-content');
    if (!target) return;

    const nextHandle = getHandleFromPathname(window.location.pathname);
    const currentHandle = target.dataset.currentHandle || null;

    if (!nextHandle || nextHandle === currentHandle) return;

    swapMainContent(nextHandle, {
      scroll: 'none',
      cache: true,
      historyMode: 'skip',
      trigger: 'popstate'
    }).catch((error) => {
      if (error && error.name !== 'AbortError') {
        console.error(error);
      }
    });
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest('[data-swap-main-content][data-product-handle]');
    if (!trigger) return;
    if (trigger.dataset.swapBusy === 'true') return;

    const handle = getHandle(trigger);
    if (!handle) return;

    const target = document.getElementById('main-content');
    if (target && target.dataset.currentHandle === handle) return;

    event.preventDefault();

    trigger.dataset.swapBusy = 'true';
    const scroll = trigger.dataset.swapScroll || 'keep';
    const cache = trigger.dataset.swapCache !== 'false';

    swapMainContent(handle, {
      scroll,
      cache,
      trigger
    }).catch((error) => {
      if (error && error.name !== 'AbortError') console.error(error);
    }).finally(() => {
      delete trigger.dataset.swapBusy;
    });
  }

  // Lightweight prefetch helper so callers can warm caches without duplicating logic.
  function prefetchMainContent(product, opts = {}) {
    const handle = getHandle(product);
    if (!handle) return Promise.resolve();
    const sectionTargets = getSectionTargets();
    if (!sectionTargets.length) return Promise.resolve();
    const sectionIds = sectionTargets.map((section) => section.id);
    const markWarm = opts.markWarm !== false;
    if (markWarm) WARMED_HANDLES.add(handle);
    return fetchSectionsData(handle, sectionIds, { cache: opts.cache !== false, signal: opts.signal })
      .catch((error) => {
        if (markWarm) WARMED_HANDLES.delete(handle);
        throw error;
      });
  }

  // Queue multiple handles for prefetching while keeping concurrency in check.
  // Prefetch multiple handles in a controlled batch so PDP carousels can warm the cache without thrashing the network.
  function prefetchMany(handles, { concurrency = 2, includeCurrent = false, defer = true } = {}) {
    if (!Array.isArray(handles) || !handles.length) return Promise.resolve();

    const currentHandle = includeCurrent ? null : document.getElementById('main-content')?.dataset.currentHandle || null;

    const queue = [];
    handles.forEach((item) => {
      const handle = getHandle(item);
      if (!handle) return;
      if (!includeCurrent && currentHandle && handle === currentHandle) return;
      if (WARMED_HANDLES.has(handle)) return;
      WARMED_HANDLES.add(handle);
      queue.push(handle);
    });

    if (!queue.length) return Promise.resolve();

    const limit = Math.max(1, Math.min(concurrency, queue.length));
    let index = 0;

    const runNext = () => {
      if (index >= queue.length) return Promise.resolve();
      const handle = queue[index++];
      return prefetchMainContent(handle)
        .catch(() => {
          WARMED_HANDLES.delete(handle);
        })
          .then(runNext);
    };

    const start = () => Promise.all(Array.from({ length: limit }, runNext)).then(() => {});

    if (!defer) {
      return start();
    }

    return new Promise((resolve) => {
      const kickoff = () => {
        start().then(resolve);
      };
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(kickoff, { timeout: 1500 });
      } else {
        setTimeout(kickoff, 0);
      }
    });
  }

  document.addEventListener('click', handleTriggerClick);
  window.addEventListener('popstate', handlePopState);
  document.addEventListener('cart:refresh', resetCache);
  document.addEventListener('cart:item-added', resetCache);
  document.addEventListener('line-item:change:end', resetCache);
  const listener = handleTriggerClick;
  const popstateListener = handlePopState;

  swapState.cache = CACHE;
  swapState.inflight = INFLIGHT;
  swapState.warmedHandles = WARMED_HANDLES;
  swapState.listener = listener;

  swapMainContent.abortActiveSwap = () => {
    if (activeSwapController) {
      activeSwapController.abort();
      activeSwapController = null;
    }
  };
  swapMainContent.prefetch = (product) => prefetchMainContent(product).catch(() => {});
  swapMainContent.prefetchMany = prefetchMany;
  swapMainContent.bind = (root = document) => {
    root.addEventListener('click', handleTriggerClick);
    return () => root.removeEventListener('click', handleTriggerClick);
  };
  swapMainContent.prefetchedHandles = WARMED_HANDLES;
  swapMainContent.__listener = listener;
  swapMainContent.__popstateListener = popstateListener;

  // export
  window.swapMainContent = swapMainContent;
})();
